import React, { useState, useEffect } from "react";
import uiData from "./data.json";
import { api } from "../../api/apiConfig";
import { useNavigate } from "react-router-dom";

const FieldRow = ({ label, children }) => (
  <div className="grid grid-cols-[42%_58%] items-center gap-2 text-sm">
    <label className="font-semibold">{label}</label>
    <div>{children}</div>
  </div>
);

const PartyMaster = () => {
  const navigate = useNavigate();

  const initForm = {};
  Object.values(uiData.partyDetailsSection).forEach((fld) => {
    initForm[fld.field] = fld.initialValue ?? "";
  });
  initForm[uiData.userChargeConfig.field] =
    uiData.userChargeConfig.initialValue ?? uiData.userChargeConfig.options[0];
  initForm[uiData.accountInfoSection.accountType.field] =
    uiData.accountInfoSection.accountType.initialValue ?? "";
  initForm[uiData.accountInfoSection.debitCreditToggle.field] =
    uiData.accountInfoSection.debitCreditToggle.initialValue ?? "";
  initForm[uiData.accountInfoSection.openingBalanceDebit.field] = "";
  initForm[uiData.accountInfoSection.openingBalanceCredit.field] = "";

  const [formData, setFormData] = useState(initForm);
  const [tableData, setTableData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const onChange = (field, val) =>
    setFormData((s) => ({ ...s, [field]: val }));

  useEffect(() => {
    api
      .get("/party_master")
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.data)) {
          const normalized = res.data.data.map((item, idx) =>
            mapServerToClient(item, idx)
          );
          setTableData(normalized);
        } else {
          setTableData([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching parties:", err);
        setTableData([]);
      });
  }, []);




  const handleSave = async () => {
    const payload = mapFormToPayload(formData);

    try {
      const { data } = await api.post("/party_master", payload);

      const normalized = mapServerToClient(data);
      setTableData((prev) => [...prev, normalized]);

      resetForm();
    } catch (err) {
      console.error("Error saving party:", err);
    }
  };


  const handleEdit = async () => {
    if (!selectedRow) return;
    const payload = mapFormToPayload(formData);

    try {
      const { data } = await api.put(`/party_master/${selectedRow.id}`, payload);

      const normalized = mapServerToClient(data);

      setTableData((prev) =>
        prev.map((row) => (row.id === selectedRow.id ? normalized : row))
      );
      resetForm();
    } catch (err) {
      console.error("Error editing party:", err);
    }
  };
  const generatePartyCode = (name, idx) => {
    const prefix = name ? name.substring(0, 3).toUpperCase() : "PTY";
    return `${prefix}${Date.now()}${idx}`;
  };

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      await api.delete(`/party_master/${selectedRow.id}`);
      setTableData((prev) => prev.filter((row) => row.id !== selectedRow.id));
      resetForm();
    } catch (err) {
      console.error("Error deleting party:", err);
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleExit = () => {
    navigate("/dashboard");
  };

  const resetForm = () => {
    setFormData(initForm);
    setSelectedRow(null);
  };

  const mapFormToPayload = (form, idx = 0) => ({
    party_code: form.partyNumber || generatePartyCode(form.partyName, idx),
    party_name: form.partyName || "",
    alias_name: form.alias_name || "",
    surname: form.surname || "",
    village_name: form.partyVillage || "",
    area_name: form.area_name || "",
    phone: form.phone || "",
    user_charge: form.user_charge || "",
    account_type: form.accountType || "",
    opening_balance: form.openingBalanceDebit || 0,
    opening_credit: form.openingBalanceCredit || 0,
  });
  const mapServerToClient = (item, idx = 0) => ({
    id: item.id,
    codeNumber: item.party_code || `P${idx + 1}`,
    accountName: item.party_name || "",
    alias_name: item.alias_name || "",
    surname: item.surname || "",
    accountVillage: item.village_name || "",
    accountArea: item.area_name || "",
    phone: item.phone || "",
    userChargeStatus: item.user_charge || "",
    accountType: item.account_type || "",
    openingBalanceDebit: item.opening_balance || 0,
    openingBalanceCredit: item.opening_credit || 0,
  });

  const actionHandlers = {
    new: handleCancel,
    save: handleSave,
    edit: handleEdit,
    delete: handleDelete,
    cancel: handleCancel,
    exit: handleExit,
  };

  return (
    <div className="bg-[#efe6c8] md:p-3">
      <div className="w-full  flex flex-col border-4 border-black bg-[#f4efd6]">
        <div className="flex flex-col md:flex-row items-center justify-between px-3 py-1 bg-[#e8e0b8] border-b-2 border-black text-xs gap-1 md:gap-0">
          <div className="truncate font-medium">{uiData.header.softwareName}</div>
          <div className="text-center text-xs">{uiData.header.companyName}</div>
          <div className="text-right">{uiData.header.versionInfo}</div>
        </div>

        <div className="py-2 border-b-2 border-black bg-[#fff5d8] text-center">
          <div className="text-xl md:text-2xl lg:text-3xl font-extrabold">
            {uiData.screenTitle}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-2 p-2 md:p-3 overflow-hidden">
          {/* Left Side - Party Details */}
          <div className="flex-1 md:flex-[3] border-2 border-black bg-[#fffde7] p-3 flex flex-col gap-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-2 items-center">
              <div>
                <label className="font-bold text-sm">
                  {uiData.partyDetailsSection.partyName.label}
                </label>
                <input
                  className="w-full border-2 border-black rounded px-2 py-1 mt-1 text-sm"
                  placeholder={uiData.partyDetailsSection.partyName.placeholder}
                  value={formData[uiData.partyDetailsSection.partyName.field] || ""}
                  onChange={(e) =>
                    onChange(uiData.partyDetailsSection.partyName.field, e.target.value)
                  }
                />
              </div>
              <div>
                <label className="font-bold text-sm block text-center">
                  {uiData.partyDetailsSection.partyNumber.label}
                </label>
                <input
                  readOnly
                  className="w-full border-2 border-black rounded px-2 py-1 mt-1 text-sm text-center bg-[#f0f0f0]"
                  value={formData[uiData.partyDetailsSection.partyNumber.field]}
                />
              </div>
            </div>

            {Object.entries(uiData.partyDetailsSection).map(([key, fld]) => {
              if (key === "partyName" || key === "partyNumber") return null;
              return (
                <FieldRow key={key} label={fld.label}>
                  <input
                    className="w-full border-2 border-black rounded px-2 py-1 text-sm"
                    placeholder={fld.placeholder}
                    value={formData[fld.field] || ""}
                    onChange={(e) => onChange(fld.field, e.target.value)}
                  />
                </FieldRow>
              );
            })}

            <div className="flex flex-wrap gap-2 mt-2">
              {uiData.userChargeConfig.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => onChange(uiData.userChargeConfig.field, opt)}
                  className={`px-3 py-1 border-2 border-black rounded text-sm ${formData[uiData.userChargeConfig.field] === opt
                    ? "bg-green-300"
                    : "bg-[#e6e6e6]"
                    }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/*  Right Side - Account Info */}
          <div className="flex-1 md:flex-[2] border-2 border-black bg-[#f7f2d9] p-3 flex flex-col gap-3">
            <div className="font-bold text-sm">
              {uiData.accountInfoSection.balanceInfoTitle}
            </div>

            <FieldRow label={uiData.accountInfoSection.accountType.label}>
              <select
                className="w-full border-2 border-black rounded px-2 py-1 text-sm"
                value={formData[uiData.accountInfoSection.accountType.field]}
                onChange={(e) =>
                  onChange(uiData.accountInfoSection.accountType.field, e.target.value)
                }
              >
                {uiData.accountInfoSection.accountType.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </FieldRow>

            <div>
              <div className="font-semibold text-sm mb-1">
                {uiData.accountInfoSection.debitCreditToggle.label}
              </div>
              <div className="flex flex-wrap gap-3">
                {uiData.accountInfoSection.debitCreditToggle.options.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="debitCredit"
                      checked={
                        formData[uiData.accountInfoSection.debitCreditToggle.field] ===
                        opt.value
                      }
                      onChange={() =>
                        onChange(uiData.accountInfoSection.debitCreditToggle.field, opt.value)
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <FieldRow label={uiData.accountInfoSection.openingBalanceDebit.label}>
              <input
                type="number"
                className="w-full border-2 border-black rounded px-2 py-1 text-sm"
                placeholder={uiData.accountInfoSection.openingBalanceDebit.placeholder}
                value={formData[uiData.accountInfoSection.openingBalanceDebit.field]}
                onChange={(e) =>
                  onChange(uiData.accountInfoSection.openingBalanceDebit.field, e.target.value)
                }
              />
            </FieldRow>

            <FieldRow label={uiData.accountInfoSection.openingBalanceCredit.label}>
              <input
                type="number"
                className="w-full border-2 border-black rounded px-2 py-1 text-sm"
                placeholder={uiData.accountInfoSection.openingBalanceCredit.placeholder}
                value={formData[uiData.accountInfoSection.openingBalanceCredit.field]}
                onChange={(e) =>
                  onChange(uiData.accountInfoSection.openingBalanceCredit.field, e.target.value)
                }
              />
            </FieldRow>
          </div>

          {/* Buttons area */}
          <div className="w-full md:w-44 border-2 border-black bg-[#efe6c3] p-3 flex flex-col gap-2 items-stretch">
            {uiData.actionButtons.map((btn, idx) => (
              <button
                key={idx}
                onClick={actionHandlers[btn.action] || (() => { })}
                className={`${btn.style} text-white font-semibold px-3 py-2 rounded text-sm w-full shadow-md`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table + Search Area */}
        <div
          className="flex-1 md:flex-[3] border-2 border-black bg-white p-2 
         overflow-auto min-w-[300px]
         max-h-[60vh] md:max-h-[230px]"
        >
          <div className="font-bold mb-2 text-sm">
            {uiData.existingAccountsTable.title}
          </div>
          <table className="w-full border-collapse text-sm min-w-[500px] ">
            <thead className="bg-[#f0f0f0] sticky top-0">
              <tr>
                {uiData.existingAccountsTable.headers.map((h) => (
                  <th
                    key={h.field}
                    className="border border-black px-2 py-1 text-left"
                  >
                    {h.label}
                  </th>
                ))}
                <th className="border border-black px-2 py-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                tableData.map((row, i) => (
                  <tr key={i}>
                    {uiData.existingAccountsTable.headers.map((h) => (
                      <td key={h.field} className="border border-black px-2 py-1">
                        {row[h.field] || "-"}
                      </td>
                    ))}
                    <td className="border border-black px-2 py-1 flex gap-1">
                      <button
                        onClick={() => {
                          setSelectedRow(row);
                          setFormData({
                            partyNumber: row.codeNumber,
                            partyName: row.accountName,
                            alias_name: row.alias_name,
                            surname: row.surname,
                            partyVillage: row.accountVillage,
                            area_name: row.area_name,
                            phone: row.phone,
                            user_charge: row.userChargeStatus,
                            accountType: row.accountType,
                            openingBalanceDebit: row.openingBalanceDebit,
                            openingBalanceCredit: row.openingBalanceCredit,
                          });
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 text-xs rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRow(row);
                          handleDelete();
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 text-xs rounded"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={uiData.existingAccountsTable.headers.length + 1}
                    className="text-center py-2"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default PartyMaster;
